

import React, { useRef, useState, useEffect, useContext, useMemo } from 'react';
import logo from "../../assets/images/log03.png";
import { useReactToPrint } from 'react-to-print';
import { ExamContext, MeContext, attendanceContext, userContext } from "../../App";
import {
  getSubjectsForClass,
  sscSub,
} from "../../data/subjectData";
import { calculateGreat, calculateMyPosition, handleGreat, maxMark, totalMarks } from "../../Utils/CalculateFunction";
import ProgressReport from './ProgressReport';

const ResultDownload = ({
  students,
  subject,
  classe,
}) => {
  const componentRef = useRef(null);
  const [me] = useContext(MeContext);
  const [xms] = useContext(ExamContext);
  const [Attendances] = useContext(attendanceContext);
  const [users] = useContext(userContext);
  
  const [xmSub, setXmSub] = useState([...sscSub]);
  const [examName, setExamName] = useState("");
  const [selectedClass, setSelectedClass] = useState(classe || "Six");
  const [classStudents, setClassStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Get current exam
  const compExam = xms?.filter((i) => i?.status?.includes( me?.role === "admin" ? "processing" || "complete" : "complete"));
  const MyXm = xms?.find((i) => i?.examName === examName);

  // Dynamic class options
  const availableClasses = useMemo(() => [
    "Play", "Nursery", "One-Boys", "One-Girls", "Two-Boys", "Two-Girls", "Three-Boys", "Three-Girls", "Four-Boys", "Four-Girls", "Five-Boys", "Five-Girls",
    "Six-Boys", "Six-Girls",  "Seven-Boys", "Seven-Girls",
     "Eight-Boys", "Eight-Girls",  "Nine-Boys", "Nine-Girls",
     "Ten-Boys", "Ten-Girls"
  ], []);

  // Dynamic subject selection based on class
  useEffect(() => {
    setXmSub(getSubjectsForClass(selectedClass));
  }, [selectedClass]);

  // Set default exam
  useEffect(() => {
    if (compExam && compExam.length > 0) {
      setExamName(compExam[0]?.examName);
    }
  }, [compExam]);

  // Enhanced student filtering with position calculation
  const filterStudentsByClass = (students, className) => {
    if (!students || !className) return [];
    
    let filteredStudents = [];
    
    if (className.includes('-Boys') || className.includes('-Girls')) {
      const baseClass = className.split('-')[0];
      const gender = className.split('-')[1] === "Boys" ? "male" : "female";
      
      filteredStudents = students
        ?.filter(student => 
          student?.class === baseClass && 
          student?.gender?.toLowerCase() === gender
        )
        ?.sort((a, b) => a.roleNo - b.roleNo) || [];
    } else {
      filteredStudents = students
        ?.filter(student => student?.class?.includes(className))
        ?.sort((a, b) => a.roleNo - b.roleNo) || [];
    }
    
    return filteredStudents;
  };

  // Filter students by class
  useEffect(() => {
    if (students && selectedClass) {
      const filteredStudents = filterStudentsByClass(students, selectedClass);
      setClassStudents(filteredStudents);
    }
  }, [students, selectedClass]);

  // Calculate class statistics with enhanced performance
  const classStatistics = useMemo(() => {
    if (!MyXm || !classStudents.length) return null;

    const classResults = classStudents.map(student => {
      const result = MyXm?.Results?.find(r => r?.studentPh === student?.phoneNumber);
      if (!result) return null;

      // Filter subjects for Class Nine and Ten (Agricultural vs Higher Math)
      let filteredSubjects = result?.subResult || [];
      if (student?.class === "Nine" || student?.class === "Ten") {
        const hasAgricultural = result?.subResult?.find(s => s?.subject === "Agricultural");
        const hasHigherMath = result?.subResult?.find(s => s?.subject === "Higher Math");
        
        if (hasAgricultural && hasHigherMath) {
          // If both exist, keep only Agricultural
          filteredSubjects = result?.subResult?.filter(s => s?.subject !== "Higher Math");
        } else if (hasHigherMath && !hasAgricultural) {
          // If only Higher Math exists, keep it
          filteredSubjects = result?.subResult;
        } else if (hasAgricultural && !hasHigherMath) {
          // If only Agricultural exists, keep it
          filteredSubjects = result?.subResult;
        }
      }

      const totalMarks = filteredSubjects?.reduce((total, subject) => {
        const m = subject?.mcq || 0;
        const w = subject?.writing || 0;
        const p = subject?.pecticNun || 0;
        return total + parseFloat(m) + parseFloat(w) + parseFloat(p);
      }, 0);

      return {
        student,
        result: { ...result, subResult: filteredSubjects },
        totalMarks,
        studentPh: student?.phoneNumber
      };
    }).filter(Boolean);

    // Sort by total marks for position calculation
    classResults.sort((a, b) => b.totalMarks - a.totalMarks);

    // Calculate additional statistics
    const totalMarksSum = classResults.reduce((sum, r) => sum + r.totalMarks, 0);
    const averageMarks = totalMarksSum / classResults.length || 0;
    const highestMarks = classResults[0]?.totalMarks || 0;
    const lowestMarks = classResults[classResults.length - 1]?.totalMarks || 0;

    return {
      totalStudents: classResults.length,
      results: classResults,
      highestMarks,
      lowestMarks,
      averageMarks,
      totalMarksSum
    };
  }, [MyXm, classStudents]);

  // Error handling
  const hasValidData = examName && classStudents.length > 0 && MyXm;
  const errorMessage = !examName ? 'Please select an exam' : 
                     classStudents.length === 0 ? 'No students found for selected class' : 
                     !MyXm ? 'Exam data not found' : null;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `${selectedClass} - ${examName} Results Report`,
    removeAfterPrint: true,
    onBeforeGetContent: () => {
      setIsLoading(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsLoading(false);
      console.log('Print completed!');
    },
    pageStyle: `
    @page {
      size: A4;
      margin: 0.5in;
      /* Remove default browser headers and footers */
      @top-left { content: ""; }
      @top-center { content: ""; }
      @top-right { content: ""; }
      @bottom-left { content: ""; }
      @bottom-center { content: ""; }
      @bottom-right { content: ""; }
    }
    
    @media print {
      /* Hide browser default headers/footers */
      @page { 
        margin: 0.5in;
        size: A4;
      }
      
      /* Ensure no browser defaults show */
      html, body {
        -webkit-print-color-adjust: exact;
        color-adjust: exact;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      body {
        font-family: Arial, sans-serif;
      }
      
      /* Hide any unwanted elements during print */
      .no-print {
        display: none !important;
      }
      
      /* Ensure content takes full width */
      .print-content {
        width: 100% !important;
        max-width: none !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Page break controls */
      .page-break {
        page-break-before: always;
        page-break-inside: avoid;
      }
      
      .page-break:first-child {
        page-break-before: auto;
      }
      
      .avoid-break {
        page-break-inside: avoid;
      }
      
      /* Ensure each student gets a full page */
      .page-break > div {
        min-height: 100vh;
        display: flex;
        flex-direction: column;
      }
    }
  `,
    onAfterPrint: () => console.log('Print completed!'),
    print: async (printIframe) => {
      // Custom print function to ensure proper formatting
      return new Promise((resolve) => {
        const document = printIframe.contentDocument;
        if (document) {
          const window = printIframe.contentWindow;
          if (window) {
            // Add additional styles to remove browser chrome
            const style = document.createElement('style');
            style.textContent = `
            @media print {
              @page {
                margin: 0.5in;
                size: A4;
              }
              
              body {
                margin: 0;
                padding: 0;
                -webkit-print-color-adjust: exact;
                color-adjust: exact;
              }
            }
          `;
            document.head.appendChild(style);

            window.focus();
            window.print();
            resolve();
          }
        }
      });
    },
  });


  return (
    <div>
      {/* Control Panel */}

      <div className="mb-4 p-4 bg-white rounded-lg shadow-md">
        <h3 className="text-lg font-bold text-purple-700 mb-3">Class Result Download</h3>
      {me?.role === "admin" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Class</label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              {availableClasses.map((className, index) => (
                <option key={index} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Exam</label>
            <select
              value={examName}
              onChange={(e) => setExamName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="">Choose an exam</option>
              {compExam?.map((exam, index) => (
                <option key={index} value={exam.examName}>
                  {exam.examName}
                </option>
              ))}
            </select>
          </div>
        </div>
  )}
        <div className="flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <div className="flex flex-col gap-1">
              <span className="font-semibold">Students in {selectedClass}: {classStudents.length}</span>
              {classStatistics && (
                <div className="text-xs text-blue-600">
                  <span>Highest: {classStatistics.highestMarks.toFixed(0)} | </span>
                  <span>Average: {classStatistics.averageMarks.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
          
          <button 
            className="btn capitalize rounded-3xl py-2 px-6 border-[3px] border-purple-500 text-lg font-reem bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handlePrint}
            disabled={!hasValidData}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Processing...
              </div>
            ) : hasValidData ? 
              `Download ${classStudents.length} Students Results` : 
              errorMessage || 'Please Select Exam & Class'
            }
          </button>
        </div>
      </div>
    

      {/* Print Content */}
      <div 
      className="overflow-hidden h-0"
      >
        <div ref={componentRef} id="pdf-content" className="bg-white">
          {classStudents?.map((student, index) => {
            // Find student's position in class
            const studentPosition = classStatistics?.results?.findIndex(r => r.studentPh === student.phoneNumber) + 1 || 0;
            
            return (
              <div key={student.phoneNumber} className={`w-[210mm] min-h-[297mm] mx-auto pt-2 p-0 relative`}
              style={{
                pageBreakAfter: 'auto',
              }}>
                <ProgressReport
                  student={student}
                  examData={MyXm}
                  xmSub={xmSub}
                  classe={selectedClass}
                  examName={examName}
                  classStatistics={classStatistics}
                  studentPosition={studentPosition}
                />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ResultDownload;